import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Lock, Mail, Loader2, Send, Search, FileText, BarChart3, Users, Zap, Target, CheckCircle2 } from "lucide-react";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Check for session_id from Google OAuth callback
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
      }
    }
  }, [location]);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
      login(response.data.access_token, response.data.user);
      toast.success(`Welcome, ${response.data.user?.name || 'User'}!`);
      window.history.replaceState(null, '', window.location.pathname);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google sign-in failed");
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data.access_token, response.data.user);
      toast.success("Welcome back!");
      navigate("/");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("verify your email")) {
        toast.error(detail, {
          action: {
            label: "Resend",
            onClick: () => resendVerification()
          }
        });
      } else {
        toast.error(detail || "Invalid email or password");
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      toast.success("Verification email sent. Please check your inbox.");
    } catch (error) {
      toast.error("Failed to resend verification email");
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-white font-medium">Signing in with Google...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="flex min-h-screen">
        {/* Left Side - Features & Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-[#165581] p-12 flex-col relative overflow-hidden">
          {/* Soft background shapes */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-sky-300/20 rounded-full blur-2xl" />
          </div>
          
          {/* Logo on blue background */}
          <div className="relative z-10 mb-12">
            <div className="flex items-center gap-4">
              <img src="/logo-blue-bg.png" alt="eSay" className="w-16 h-16" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">eSay</h1>
                <p className="text-white/80 text-sm font-medium">Email Marketing</p>
              </div>
            </div>
          </div>
          
          {/* Tagline */}
          <div className="relative z-10 mb-12">
            <h2 className="text-4xl font-bold text-white leading-tight mb-4">
              Email Marketing.<br/>
              <span className="text-white/90">Start to Finish.</span>
            </h2>
            <p className="text-lg text-white/80 max-w-md leading-relaxed">
              The complete email marketing platform for growing businesses.
            </p>
          </div>
          
          {/* Feature Cards */}
          <div className="relative z-10 space-y-4 flex-1">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Find Business Leads</h3>
                  <p className="text-white/70 text-sm">Search Google Maps for businesses by category and location. Get emails, phone numbers, and addresses instantly.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Beautiful Email Templates</h3>
                  <p className="text-white/70 text-sm">Create professional emails with our rich text editor. Personalise with dynamic placeholders.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-400 flex items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Send via Gmail</h3>
                  <p className="text-white/70 text-sm">Connect your Gmail account and send campaigns directly. Better deliverability than cold email tools.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Track Performance</h3>
                  <p className="text-white/70 text-sm">Monitor opens, clicks, and bounces in real-time. A/B test subject lines to optimise results.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="relative z-10 mt-8 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span className="text-white/70 text-sm">No payment details required to get started</span>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <Card className="border border-slate-200 shadow-xl bg-white" data-testid="login-card">
              <CardHeader className="space-y-1 text-center pb-2">
                {/* Logo on white - shown above login form */}
                <div className="flex items-center justify-center mb-4">
                  <img src="/logo-white-bg.png" alt="eSay" className="w-16 h-16" />
                </div>
                <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
                  Welcome Back
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Sign in to your eSay account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 h-12 border-slate-200 focus:border-[#165581] focus:ring-[#165581]"
                      data-testid="email-input"
                      required
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-11 h-12 border-slate-200 focus:border-[#165581] focus:ring-[#165581]"
                      data-testid="password-input"
                      required
                    />
                  </div>
                  <div className="text-right">
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-[#165581] hover:text-[#1a6a9e] font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#165581] hover:bg-[#1a6a9e] text-white font-semibold shadow-lg shadow-[#165581]/25 hover:shadow-[#165581]/40 transition-all"
                    disabled={loading}
                    data-testid="login-button"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Signing in...
                      </span>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-3 text-slate-400 font-medium">Or continue with</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 border-slate-200 hover:bg-slate-50 font-medium"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <p className="text-center text-sm text-slate-500 mt-6">
                  Don't have an account?{" "}
                  <Link 
                    to="/register" 
                    className="text-[#165581] hover:text-[#1a6a9e] font-semibold"
                    data-testid="register-link"
                  >
                    Sign up
                  </Link>
                </p>
              </CardContent>
            </Card>
            
            {/* Mobile-only features summary */}
            <div className="lg:hidden mt-8 space-y-3">
              <h3 className="text-center text-slate-700 font-semibold mb-4">Why eSay?</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <Search className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Find Leads</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <FileText className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Email Templates</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <Send className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Gmail Sending</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <BarChart3 className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Analytics</p>
                </div>
              </div>
              
              {/* Legal links for mobile */}
              <div className="text-center text-xs text-slate-400 mt-4">
                <Link to="/privacy" className="hover:text-slate-600">Privacy Policy</Link>
                <span className="mx-2">•</span>
                <Link to="/terms" className="hover:text-slate-600">Terms of Service</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer with legal links */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-slate-200 py-3 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 flex justify-center gap-6 text-sm text-slate-500">
          <Link to="/privacy" className="hover:text-[#165581]">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#165581]">Terms of Service</Link>
          <span>&copy; {new Date().getFullYear()} eSay</span>
        </div>
      </div>
    </div>
  );
};
